import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import classNames from 'styles/utils/classNames';

export interface ModalProps {
  isOpen: boolean;
  ariaLabel?: string;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
  handleClose: (value?: boolean) => void;
  classNameRounded?: string;
  classNameMaxWidth?: string;
  classNamePosition?: string;
  classNameHeight?: string;
  className?: string;
  positionBottomDesktop?: boolean;
  centerOnMobile?: boolean;
  appear?: boolean;
  children?: React.ReactNode;
  dialogClassName?: string;
  overlayClassName?: string;
}

export const EXIT_DURATION_MS = 200;
export const EXIT_DURATION_SAFE_MS = EXIT_DURATION_MS + 300;

// NOTE: How to handle widths and max widths??
export default function Modal({
  isOpen,
  ariaLabel,
  initialFocus,
  handleClose,
  classNameRounded,
  classNameMaxWidth,
  classNamePosition,
  classNameHeight,
  className,
  positionBottomDesktop,
  centerOnMobile = false,
  appear,
  children,
  dialogClassName = 'z-50',
  overlayClassName,
}: ModalProps) {
  return (
    <Transition.Root appear={appear} show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className={classNames('relative', dialogClassName)}
        aria-label={ariaLabel}
        onClose={handleClose}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames(
              'fixed inset-0 transition-opacity',
              overlayClassName ?? 'bg-gray-800 bg-opacity-30 backdrop-blur-sm',
            )}
          />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className={classNames(
              'flex min-h-full justify-center',
              centerOnMobile ? 'items-center' : 'items-end',
              positionBottomDesktop ? 'sm:items-end' : 'sm:items-center',
              !!classNameHeight && classNameHeight,
            )}
          >
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                className={classNames(
                  'flex w-full items-center justify-center',
                  !!classNameHeight && classNameHeight,
                )}
              >
                <Dialog.Panel
                  className={classNames(
                    'bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary w-full transform overflow-hidden shadow-xl transition-all sm:my-4 sm:w-full',
                    classNameRounded ? classNameRounded : 'rounded-t-3xl sm:rounded-2xl',
                    classNamePosition ? classNamePosition : 'relative',
                    classNameMaxWidth ? classNameMaxWidth : 'sm:max-w-md',
                    !!className && className,
                  )}
                >
                  {children}
                  <div className="safearea-spacer-bot w-full"></div>
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
